import "./UploadProgressToaster.scss";

import { useState } from "react";

export type UploadProgress = {
  id: string;
  filename: string;
  totalBytes: number;
  uploadedBytes: number;
  percentage: number;
  speedBytesPerSecond: number;
  estimatedSecondsRemaining: number;
  status: "uploading" | "complete" | "error" | "cancelled";
  errorMessage?: string;
};

type UploadProgressToasterProps = {
  upload: UploadProgress;
  onCancel?: () => void;
  onDismiss?: () => void;
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
};

const formatSpeed = (bytesPerSecond: number): string => {
  return `${formatBytes(bytesPerSecond)}/s`;
};

const formatTime = (seconds: number): string => {
  if (seconds < 0 || !isFinite(seconds)) return "calculating...";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const UploadProgressToaster = ({
  upload,
  onCancel,
  onDismiss,
}: UploadProgressToasterProps) => {
  const [isMinimized, setIsMinimized] = useState(false);

  if (isMinimized) {
    const getMinimizedIcon = () => {
      if (upload.status === "complete") return "check_circle";
      if (upload.status === "error") return "error";
      if (upload.status === "cancelled") return "cancel";
      return "upload";
    };

    const getMinimizedText = () => {
      if (upload.status === "complete") return "Done";
      if (upload.status === "error") return "Failed";
      if (upload.status === "cancelled") return "Cancelled";
      return `${upload.percentage}%`;
    };

    return (
      <div
        className={`UploadProgressToaster UploadProgressToaster--minimized UploadProgressToaster--${upload.status}`}
      >
        <div className="upload-minimized-content">
          <span className="material-icons">{getMinimizedIcon()}</span>
          <span className="upload-minimized-text">{getMinimizedText()}</span>
          <button
            className="upload-action-button"
            onClick={() => setIsMinimized(false)}
            aria-label="Expand"
          >
            <span className="material-icons">expand_less</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`UploadProgressToaster UploadProgressToaster--${upload.status}`}
    >
      <div className="upload-header">
        <div className="upload-header-left">
          <span className="material-icons upload-icon">
            {upload.status === "uploading" && "upload"}
            {upload.status === "complete" && "check_circle"}
            {upload.status === "error" && "error"}
            {upload.status === "cancelled" && "cancel"}
          </span>
          <div className="upload-info">
            <div className="upload-filename" title={upload.filename}>
              {upload.filename}
            </div>
            {upload.status === "error" && upload.errorMessage && (
              <div className="upload-error-message">{upload.errorMessage}</div>
            )}
            {upload.status === "cancelled" && (
              <div className="upload-cancelled-message">Upload cancelled</div>
            )}
          </div>
        </div>
        <div className="upload-header-actions">
          <button
            className="upload-action-button"
            onClick={() => setIsMinimized(true)}
            aria-label="Minimize"
          >
            <span className="material-icons">minimize</span>
          </button>
          {upload.status === "uploading" && onCancel && (
            <button
              className="upload-action-button upload-cancel-button"
              onClick={onCancel}
              aria-label="Cancel upload"
            >
              <span className="material-icons">close</span>
            </button>
          )}
          {(upload.status === "complete" ||
            upload.status === "error" ||
            upload.status === "cancelled") &&
            onDismiss && (
              <button
                className="upload-action-button"
                onClick={onDismiss}
                aria-label="Dismiss"
              >
                <span className="material-icons">close</span>
              </button>
            )}
        </div>
      </div>

      {upload.status === "uploading" && (
        <>
          <div className="upload-progress-container">
            <div
              className="upload-progress-bar"
              style={{ width: `${upload.percentage}%` }}
            >
              <div className="upload-progress-shine" />
            </div>
          </div>

          <div className="upload-stats">
            <div className="upload-stat upload-percentage">
              {upload.percentage}%
            </div>
            <div className="upload-stat upload-sizes">
              {formatBytes(upload.uploadedBytes)} /{" "}
              {formatBytes(upload.totalBytes)}
            </div>
            {upload.speedBytesPerSecond > 0 && (
              <>
                <div className="upload-stat upload-speed">
                  {formatSpeed(upload.speedBytesPerSecond)}
                </div>
                <div className="upload-stat upload-eta">
                  ~{formatTime(upload.estimatedSecondsRemaining)}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {upload.status === "complete" && (
        <div className="upload-complete-message">
          Upload complete • {formatBytes(upload.totalBytes)}
        </div>
      )}
    </div>
  );
};

export default UploadProgressToaster;
